import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Tables } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { queryKeys } from "./keys";
import { operationProgressiveEnabled } from "@/lib/operation-progressive";

export type Operation = Tables<"operations">;

export type OperationWithSociete = Operation & {
  societe: Pick<Tables<"societes">, "id" | "nom" | "slug" | "groupe_id"> | null;
  groupe: Pick<Tables<"groupes">, "id" | "nom" | "slug"> | null;
  filiale: Pick<Tables<"filiales">, "id" | "nom" | "slug"> | null;
};

const OPERATION_WITH_SOCIETE_SELECT = `
  *,
  societe:societes!operations_societe_id_fkey ( id, nom, slug, groupe_id ),
  groupe:groupes!operations_groupe_id_fkey ( id, nom, slug ),
  filiale:filiales!operations_filiale_id_fkey ( id, nom, slug )
`;

/**
 * Hook : opérations accessibles à l'utilisateur courant (filtré par RLS).
 */
export function useOperations() {
  return useQuery({
    queryKey: queryKeys.operation.list(),
    queryFn: async (): Promise<OperationWithSociete[]> => {
      const { data, error } = await supabase
        .from("operations")
        .select(OPERATION_WITH_SOCIETE_SELECT)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as OperationWithSociete[];
    },
  });
}

// =============================================================================
// Paramétrage du parcours de simulation (porté par l'opération)
// =============================================================================
// Trois leviers administrés dans le formulaire d'opération :
//   - `simulation_max_age_policy` : borne MAXIMALE de départ simulable,
//   - `rachat_enabled`            : simulation avec rachat de trimestres,
//   - `simulations_max_rolling_12m` : plafond de calculs sur 12 mois glissants.
// Les mêmes règles sont appliquées côté serveur par l'edge function
// harvest-pension (helpers allowedScenarioKeys / maxDepartDate) : garder les
// deux implémentations alignées.
// =============================================================================

export type SimulationMaxAgePolicy = "taux_plein_naturel" | "age_65" | "age_67" | "libre";

// =============================================================================
// Type de parcours (preset VERROUILLÉ de capacités, porté par l'opération)
// =============================================================================
// Le client distingue trois parcours :
//   - `digital`        : salarié autonome, éloigné de la retraite. Pas de RDV,
//                        pas de hotline, pas de prise en charge production Aon.
//   - `digital_humain` : le salarié renseigne, Aon traite, RDV + restitution
//                        (rachat, retraite progressive, relevé d'anomalies).
//   - `vip`            : idem + questions complémentaires, comparaison de
//                        scénarios, arbitrage et ROI Tranche C.
// La retraite progressive peut être réglée indépendamment du preset. Les réglages
// « tout parcours » (borne d'âge, rachat, formation, quota de simulations)
// restent, eux, communs aux trois types et administrables séparément.
// MIROIR SERVEUR : supabase/functions/_shared/parcours-capabilities.ts.
// Garder les deux fichiers alignés.
// =============================================================================

export type ParcoursType = "digital" | "digital_humain" | "vip";

export const PARCOURS_TYPES: readonly ParcoursType[] = ["digital", "digital_humain", "vip"];

export interface ParcoursCapabilities {
  /** Prise de rendez-vous de restitution par le salarié. */
  appointments: boolean;
  /** Assistance téléphonique (hotline). */
  hotline: boolean;
  /** Assistant IA Noa. */
  aiAssistant: boolean;
  /** Messagerie avec l'équipe. */
  messaging: boolean;
  /** Le dossier est pris en charge par la production Aon. */
  aonProduction: boolean;
  /** Relevé d'anomalies de carrière. */
  anomaliesBank: boolean;
  /** Restitution du coût / gain de rachat de trimestres. */
  rachatRestitution: boolean;
  /** Volet retraite progressive. */
  retraiteProgressive: boolean;
  /** Questions complémentaires VIP. */
  vipQuestions: boolean;
  /** Arbitrage et ROI Tranche C. */
  tranchecArbitrage: boolean;
  /** Analyse comparée des scénarios entre eux. */
  scenarioComparison: boolean;
  /** Scénario chômage (activé par l'opération ou demandé par le RH). */
  chomageScenario: boolean;
}

/** Table de vérité unique : type de parcours → capacités. */
export const PARCOURS_CAPABILITIES: Record<ParcoursType, ParcoursCapabilities> = {
  digital: {
    appointments: false,
    hotline: false,
    aiAssistant: true,
    messaging: true,
    aonProduction: false,
    anomaliesBank: true,
    rachatRestitution: false,
    retraiteProgressive: false,
    vipQuestions: false,
    tranchecArbitrage: false,
    scenarioComparison: false,
    chomageScenario: false,
  },
  digital_humain: {
    appointments: true,
    hotline: true,
    aiAssistant: true,
    messaging: true,
    aonProduction: true,
    anomaliesBank: true,
    rachatRestitution: true,
    retraiteProgressive: true,
    vipQuestions: false,
    tranchecArbitrage: false,
    scenarioComparison: true,
    chomageScenario: false,
  },
  vip: {
    appointments: true,
    hotline: true,
    aiAssistant: true,
    messaging: true,
    aonProduction: true,
    anomaliesBank: true,
    rachatRestitution: true,
    retraiteProgressive: true,
    vipQuestions: true,
    tranchecArbitrage: true,
    scenarioComparison: true,
    chomageScenario: true,
  },
};

/** Clés de capacités, dans l'ordre d'affichage du récapitulatif admin. */
export const PARCOURS_CAPABILITY_KEYS: readonly (keyof ParcoursCapabilities)[] = [
  "aonProduction",
  "appointments",
  "hotline",
  "aiAssistant",
  "messaging",
  "anomaliesBank",
  "rachatRestitution",
  "retraiteProgressive",
  "scenarioComparison",
  "vipQuestions",
  "tranchecArbitrage",
  "chomageScenario",
];

/** Normalise une valeur brute de `operations.parcours_type`. */
export function toParcoursType(raw: unknown): ParcoursType {
  return PARCOURS_TYPES.includes(raw as ParcoursType) ? (raw as ParcoursType) : "digital_humain";
}

export const SIMULATION_MAX_AGE_POLICIES: readonly SimulationMaxAgePolicy[] = [
  "taux_plein_naturel",
  "age_65",
  "age_67",
  "libre",
];

export interface OperationParcoursSettings {
  maxAgePolicy: SimulationMaxAgePolicy;
  rachatEnabled: boolean;
  /** null = illimité. */
  simulationsMaxRolling12m: number | null;
  parcoursType: ParcoursType;
  /** null = réglage historique du type de parcours. */
  retraiteProgressiveEnabled: boolean | null;
  /** Scénario chômage ouvert à toute l'opération. */
  chomageScenarioEnabled: boolean;
}

// Dossier explicitement SANS opération (operationId === null) : aucune campagne
// cliente ne l'encadre, donc aucune borne — sinon ces dossiers seraient plus
// restreints que ceux rattachés à une opération.
const NO_OPERATION_SETTINGS: OperationParcoursSettings = {
  maxAgePolicy: "libre",
  rachatEnabled: true,
  simulationsMaxRolling12m: null,
  parcoursType: "digital_humain",
  retraiteProgressiveEnabled: null,
  chomageScenarioEnabled: false,
};

// Opération attendue mais ILLISIBLE (RLS) ou introuvable : on ne débride SURTOUT
// pas, on retombe sur la borne la plus stricte. Le serveur reste l'autorité.
const FALLBACK_PARCOURS_SETTINGS: OperationParcoursSettings = {
  maxAgePolicy: "taux_plein_naturel",
  rachatEnabled: true,
  simulationsMaxRolling12m: null,
  // Opération illisible : on retombe sur le parcours le plus restreint.
  parcoursType: "digital",
  retraiteProgressiveEnabled: false,
  chomageScenarioEnabled: false,
};

/**
 * Réglages du parcours de simulation pour une opération. `operationId` à
 * `undefined` = dossier pas encore chargé (requête désactivée), `null` = dossier
 * sans opération (aucune borne).
 */
export function useOperationParcoursSettings(operationId: string | null | undefined) {
  return useQuery({
    enabled: operationId !== undefined,
    queryKey: ["operation", "parcours-settings", operationId ?? "none"] as const,
    queryFn: async (): Promise<OperationParcoursSettings> => {
      if (!operationId) return NO_OPERATION_SETTINGS;
      const { data, error } = await supabase
        .from("operations")
        .select(
          "simulation_max_age_policy, rachat_enabled, simulations_max_rolling_12m, parcours_type, chomage_scenario_enabled, retraite_progressive_enabled",
        )
        .eq("id", operationId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return FALLBACK_PARCOURS_SETTINGS;
      const policy = SIMULATION_MAX_AGE_POLICIES.includes(
        data.simulation_max_age_policy as SimulationMaxAgePolicy,
      )
        ? (data.simulation_max_age_policy as SimulationMaxAgePolicy)
        : "taux_plein_naturel";
      const max = data.simulations_max_rolling_12m;
      return {
        maxAgePolicy: policy,
        rachatEnabled: data.rachat_enabled !== false,
        simulationsMaxRolling12m: typeof max === "number" && max > 0 ? max : null,
        parcoursType: toParcoursType(data.parcours_type),
        retraiteProgressiveEnabled: data.retraite_progressive_enabled,
        chomageScenarioEnabled: data.chomage_scenario_enabled === true,
      };
    },
  });
}

/**
 * Scénarios de départ autorisés par la borne. Le taux plein naturel et les
 * départs plus précoces restent toujours proposés.
 */
export function allowedScenarioKeys(policy: SimulationMaxAgePolicy): string[] {
  // « rachat_taux_plein » n'est pas un scénario de date de départ : il est
  // autorisé quelle que soit la borne d'âge et filtré par `rachatEnabled`.
  const base = [
    "des_que_possible",
    "age_legal",
    "age_62",
    "taux_plein_naturel",
    "rachat_taux_plein",
  ];
  if (policy === "age_65") return [...base, "age_65"];
  if (policy === "age_67" || policy === "libre") return [...base, "age_65", "age_67"];
  return base;
}

/**
 * Date de départ maximale saisissable librement (`YYYY-MM-DD`).
 * `null` = aucune date libre autorisée par la borne.
 */
export function maxDepartDate(
  policy: SimulationMaxAgePolicy,
  birthIso: string | null | undefined,
): string | null {
  if (policy === "libre") return null;
  const years = policy === "age_65" ? 65 : policy === "age_67" ? 67 : 0;
  if (years === 0 || !birthIso) return null;
  const d = new Date(birthIso);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCFullYear(d.getUTCFullYear() + years);
  return d.toISOString().slice(0, 10);
}

/** Toutes les clés de scénarios de départ gérées par la plateforme. */
export const ALL_SCENARIO_KEYS: readonly string[] = [
  "des_que_possible",
  "age_legal",
  "age_62",
  "age_65",
  "taux_plein_naturel",
  "age_67",
  "rachat_taux_plein",
];

/**
 * Bornes effectives appliquées au salarié dans le parcours.
 */
export interface SimulationLimits {
  /** Clés de scénarios de départ proposables. */
  allowedScenarios: string[];
  /** Date de départ maximale saisissable, `null` = aucune date libre. */
  maxDate: string | null;
  /** Rachat de trimestres autorisé par l'opération. */
  rachatEnabled: boolean;
  /** Aucune borne (staff Aon, dossier débridé, ou borne « libre »). */
  unrestricted: boolean;
}

/**
 * Résout les bornes du parcours. Source unique de vérité côté client, partagée
 * par toutes les étapes pour éviter que deux écrans divergent.
 *
 * `settings` à `undefined` = réglages pas encore chargés : on borne AU PLUS
 * STRICT en attendant, jamais l'inverse (sinon un écran s'ouvre en grand le
 * temps du chargement, ou définitivement si la lecture échoue).
 */
export function resolveSimulationLimits(args: {
  settings: OperationParcoursSettings | undefined;
  isAonInternal: boolean;
  datesLibresAutorisees: boolean;
  birthIso: string | null | undefined;
}): SimulationLimits {
  const { settings, isAonInternal, datesLibresAutorisees, birthIso } = args;
  const policy = settings?.maxAgePolicy ?? "taux_plein_naturel";
  const unrestricted = isAonInternal || datesLibresAutorisees || policy === "libre";
  return {
    allowedScenarios: unrestricted ? [...ALL_SCENARIO_KEYS] : allowedScenarioKeys(policy),
    maxDate: unrestricted ? null : maxDepartDate(policy, birthIso),
    // Le rachat n'est pas un enjeu de borne : on le laisse visible tant que les
    // réglages ne sont pas connus (le serveur l'écarte de toute façon).
    rachatEnabled: settings?.rachatEnabled !== false,
    unrestricted,
  };
}

/**
 * Résout les capacités effectives du parcours d'un dossier.
 *
 * `settings` à `undefined` = pas encore chargé : on retient le preset le PLUS
 * RESTREINT (`digital`), jamais l'inverse, pour ne rien ouvrir par accident le
 * temps du chargement ou en cas d'échec de lecture.
 *
 * Le staff Aon n'est pas bridé sur ce qu'il consulte, MAIS les capacités
 * strictement liées au type de parcours du client (questions VIP, arbitrage
 * Tranche C) restent gouvernées par l'opération : afficher un questionnaire VIP
 * sur une opération standard n'aurait aucun sens métier.
 */
export function resolveParcoursCapabilities(args: {
  settings: OperationParcoursSettings | undefined;
  isAonInternal?: boolean;
  /** Le RH a demandé le scénario chômage pour ce dossier précis. */
  chomageRequested?: boolean;
}): ParcoursCapabilities {
  const { settings, isAonInternal = false, chomageRequested = false } = args;
  const type = settings?.parcoursType ?? "digital";
  const base = PARCOURS_CAPABILITIES[type];
  return {
    ...base,
    retraiteProgressive: operationProgressiveEnabled(type, settings?.retraiteProgressiveEnabled),
    // Le staff garde l'accès aux outils de production quel que soit le parcours.
    aonProduction: base.aonProduction || isAonInternal,
    anomaliesBank: base.anomaliesBank || isAonInternal,
    // Scénario chômage : preset du parcours, ou activation opération, ou
    // demande explicite du RH sur ce dossier.
    chomageScenario:
      base.chomageScenario || settings?.chomageScenarioEnabled === true || chomageRequested,
  };
}

/**
 * Hook prêt à l'emploi : capacités du parcours pour un dossier.
 *
 * `operationId` à `undefined` = dossier pas encore chargé : `isLoading` reste
 * vrai et les capacités renvoyées sont les plus restreintes.
 */
export function useParcoursCapabilities(args: {
  operationId: string | null | undefined;
  chomageRequested?: boolean;
}): {
  capabilities: ParcoursCapabilities;
  parcoursType: ParcoursType | undefined;
  settings: OperationParcoursSettings | undefined;
  isLoading: boolean;
} {
  const { isAonInternal } = useAuth();
  const { data: settings, isLoading } = useOperationParcoursSettings(args.operationId);
  return {
    capabilities: resolveParcoursCapabilities({
      settings,
      isAonInternal,
      chomageRequested: args.chomageRequested,
    }),
    parcoursType: settings?.parcoursType,
    settings,
    isLoading: args.operationId === undefined || isLoading,
  };
}

/** Les dates libres sont-elles saisissables sous cette borne ? */
export function allowsFreeDates(
  policy: SimulationMaxAgePolicy,
  birthIso: string | null | undefined,
): boolean {
  return policy === "libre" || maxDepartDate(policy, birthIso) !== null;
}

/**
 * Hook : opération par id.
 */
export function useOperation(operationId: string | undefined) {
  return useQuery({
    queryKey: operationId ? queryKeys.operation.byId(operationId) : ["operation", "none"],
    queryFn: async (): Promise<OperationWithSociete | null> => {
      if (!operationId) return null;
      const { data, error } = await supabase
        .from("operations")
        .select(OPERATION_WITH_SOCIETE_SELECT)
        .eq("id", operationId)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as OperationWithSociete | null;
    },
    enabled: Boolean(operationId),
  });
}
